import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Settings2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";
import { FilterBar, buildQuery } from "@/components/FilterBar";
import PageHeader, { EmptyState, fmtDate } from "@/components/PageHeader";
import { shortNo } from "@/lib/caseNo";

export default function AdminLettering() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState({ district_id: "", campus_id: "", team_id: "", year: "" });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const q = buildQuery(filter);
    const { data } = await api.get(`/admin/lettering?${q}${q ? "&" : ""}page=${page}&page_size=25`);
    setItems(data.items); setTotal(data.total);
  }, [filter, page]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await api.put(`/admin/lettering/${editing.id}`, { status }); toast.success("Updated"); setEditing(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Letter Points Management" subtitle="Global view of all lettering submissions"
        actions={<Button variant="outline" className="rounded-full" onClick={() => navigate("/admin/achievements")} data-testid="achievement-options-btn"><Settings2 className="mr-2 h-4 w-4" /> Achievement Options</Button>} />
      <FilterBar value={filter} onChange={(v) => { setFilter(v); setPage(1); }} />
      {items.length === 0 ? <EmptyState title="No submissions" testId="admin-lettering-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="admin-lettering-table">
            <TableHeader><TableRow className="bg-slate-50">{["Case #", "Member", "Campus", "Team", "Achievement", "Pts", "Status", "Archived", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`admin-lettering-row-${s.case_number}`}>
                  <TableCell className="font-mono text-xs font-semibold">{shortNo(s.case_number)}</TableCell>
                  <TableCell className="text-sm">{s.team_member_name}</TableCell>
                  <TableCell className="text-sm">{s.campus_name}</TableCell>
                  <TableCell className="text-sm">{s.team_code}</TableCell>
                  <TableCell className="text-sm">{s.achievement_label_snapshot}</TableCell>
                  <TableCell className="font-bold tabular-nums text-violet-600">{s.points_requested_snapshot}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-xs">{s.archived ? "Yes" : "No"}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => { setEditing(s); setStatus(s.status); }} data-testid={`admin-edit-lettering-${s.case_number}`}>Edit</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Pagination page={page} pageSize={25} total={total} onPage={setPage} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="admin-lettering-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit {shortNo(editing?.case_number)}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-lettering-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["pending", "approved", "disapproved", "returned_for_correction"].map((s) => <SelectItem key={s} value={s}>{s === "disapproved" ? "declined" : s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="admin-lettering-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
